import Head from 'next/head'
import Renderer, { ComponentCatalog } from '@olxbr/clodoview-react-renderer'
import ClodoviewContext from '@olxbr/clodoview-react-context'
import { Pos } from '@/models/pos'
import { getPOS, tryParseJson } from '@/clients/bff'
import { loadContext } from '@/utils/request'
import { createRequestLogger, parseObject } from '@/utils/logger'
import dynamic from 'next/dynamic'
import Lurker from '@/components/lurker'
import {
  ENABLE_MOUSEFLOW,
  ENABLE_GOOGLE_GTM,
  ENABLE_GOOGLE_OPTIMIZE,
  ENABLE_LURKER,
  ENABLE_WEB_VITALS,
  DD,
  VERSION,
  GTM_ID_VR,
  GTM_ID_OLX,
} from '@/utils/constants'
import {
  GoogleGtm,
  GoogleGtmNoScript,
  GoogleOptimize,
} from '@/components/google'
import { HTTP_STATUS_CODE, REDIRECT_STATUS_LIST } from '@/utils/httpStatusCode'
import ErrorBoundary from '@/components/ErrorBoundary'
import Mouseflow from '@/components/mouseflow'
import { useMemo } from 'react'
import { sendDataDogError } from '@/components/datadog-init'
import { sendToDataDog } from '@/utils/datadog'
import { getBusinessUnitFromURL } from '@/utils/businessUnit'
import { buildHashedUserData } from '@/utils/hash'
import type { HashedUserData } from '@/utils/hash'

interface LoadScripts {
  lurker: boolean
  googleOptimize: boolean
  googleGtm: {
    enabled: boolean
    id: string
  }
  webVitals: boolean
  mouseflow: boolean
}

interface PageProps {
  pos: Pos
  loadScripts: LoadScripts
  businessUnit: string
  publicAccountId?: string
  hashedUserData?: HashedUserData
}

export default function Home(props: Readonly<PageProps>) {
  const { pos, loadScripts, publicAccountId } = props

  const catalog: ComponentCatalog = {
    Header: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Header)
    ),
    HeaderDS: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.HeaderDS)
    ),
    FocusHeaderDS: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.FocusHeaderDS)
    ),
    Footer: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Footer)
    ),
    TextButtonBox: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.TextButtonBox)
    ),
    BoxWithOLXSpot: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.BoxWithOLXSpot)
    ),
    BannerImage: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.BannerImage)
    ),
    BannerImageAndText: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.BannerImageAndText
      )
    ),
    BannerWithTimer: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.BannerWithTimer)
    ),
    BannerImageAndTextSmall: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.BannerImageAndTextSmall
      )
    ),
    BannerTextWithTimer: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.BannerTextWithTimer
      )
    ),
    Container: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Container)
    ),
    DsText: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.DsText)
    ),
    TextBox: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.TextBox)
    ),
    TextWithLink: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.TextWithLink)
    ),
    TextWithIcons: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.TextWithIcons)
    ),
    IconTextList: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.IconTextList)
    ),
    DsContainer: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.DsContainer)
    ),
    BuyFooterWrapper: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.BuyFooterWrapper)
    ),
    ListCardsWithIconInformative: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ListCardsWithIconInformative
      )
    ),
    ProductCardCompact: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ProductCardCompact
      )
    ),
    Testimonial: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Testimonial)
    ),
    Carousel: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Carousel)
    ),
    StampTitleDescription: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.StampTitleDescription
      )
    ),
    GroupCards: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.GroupCards)
    ),
    ClickToShowBox: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.ClickToShowBox)
    ),
    ImageTitleDescription: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ImageTitleDescription
      )
    ),
    ListProductsCards: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.ListProductsCards)
    ),
    TimeOnPageEvent: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.TimeOnPageEvent)
    ),
    AccordionsGroup: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.AccordionsGroup)
    ),
    Grid: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Grid)
    ),
    BannerSpotAndText: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.BannerSpotAndText)
    ),
    ProductCardAccordions: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ProductCardAccordions
      )
    ),
    ProductCard: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.ProductCard)
    ),
    ProductCardWithRadio: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ProductCardWithRadio
      )
    ),
    RenderByValue: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.RenderByValue)
    ),
    SwitchTabs: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.SwitchTabs)
    ),
    MouseOutConfirmModal: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.MouseOutConfirmModal
      )
    ),
    MarkdownText: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.MarkdownText)
    ),
    ProductFeaturesList: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ProductFeaturesList
      )
    ),
    GridWithTitle: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.GridWithTitle)
    ),
    ToggleSwitchRender: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ToggleSwitchRender
      )
    ),
    HeaderZap: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then((mod) => mod.HeaderZap)
    ),
    Divider: dynamic(() => import(`@olxbr/ds-web`).then((mod) => mod.Divider)),
    ZapSteps: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then((mod) => mod.ZapSteps)
    ),
    RenderByExperiment: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.RenderByExperiment
      )
    ),

    CardWithSpotTextButton: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.CardWithSpotTextButton
      )
    ),
    ProductCardSelect: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.ProductCardSelect
      )
    ),
    ProductsCardsSelectPage: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.ProductsCardsSelectPage
      )
    ),
    ProductCardBasic: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.ProductCardBasic
      )
    ),
    InfoBoxWithSpot: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.InfoBoxWithSpot)
    ),
    SpotTextList: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.SpotTextList)
    ),
    CheckTextList: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.CheckTextList)
    ),
    DsFlex: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.DsFlex)
    ),
    PositionWrapper: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.PositionWrapper)
    ),
    ButtonLink: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.ButtonLink)
    ),
    DsSpot: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.DsSpot)
    ),
    OptionsNavigation: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.OptionsNavigation)
    ),
    ProductCardsGroup: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.ProductCardsGroup)
    ),
    SelectPill: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.SelectPill)
    ),
    SelectOptions: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.SelectOptions)
    ),
    ToggleSwitchWithBadge: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.ToggleSwitchWithBadge
      )
    ),
    DropdownWithTitle: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.DropdownWithTitle)
    ),

    ProductCardBasicV3: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.ProductCardBasicV3
      )
    ),
    ProductsSelectPageV3: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.ProductsSelectPageV3
      )
    ),
    Box: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Box)
    ),
    Image: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.Image)
    ),
    IziProductsSelectPage: dynamic(() =>
      import(`@olxbr/zap-sales-react-components`).then(
        (mod) => mod.IziProductsSelectPage
      )
    ),
    SatisfactionSurvey: dynamic(() =>
      import(`@olxbr/pos-react-components`).then(
        (mod) => mod.SatisfactionSurvey
      )
    ),
    StatCard: dynamic(() =>
      import(`@olxbr/pos-react-components`).then((mod) => mod.StatCard)
    ),
  }
  const data: Record<string, any> = pos.data ?? {}
  const env: Record<string, any> = data.env ?? {}
  const description = (data.description as string) || `OLX Pos`
  const version = (data.version || `0.0.0`) as string
  const platform = (env.platform || `web`) as string
  const clodoviewContext = useMemo(
    () => ({
      sendErrorEvent: (errorEvent: any) => {
        const context = {
          slug: pos.key || ``,
          ...env,
        }
        sendDataDogError(new Error(`Clodoview Render Error`), {
          ...errorEvent,
          ...context,
          errorType: `ClodoviewRender`,
        })
      },
      appVersion: version,
      platform: platform,
    }),
    [pos, env, version, platform]
  )

  return (
    <>
      <Lurker
        enabled={loadScripts.lurker}
        data={pos.data}
        webVitalsEnabled={loadScripts.webVitals}
        publicAccountId={publicAccountId}
        businessUnit={env.businessUnit.toLowerCase()}
        hashedUserData={props.hashedUserData}
      />
      <GoogleGtm
        enabled={loadScripts.googleGtm.enabled}
        id={loadScripts.googleGtm.id}
      />
      <GoogleOptimize enabled={loadScripts.googleOptimize} />
      <Head>
        <title>{props.businessUnit}</title>
        <meta name="description" content={description} />
      </Head>
      <ErrorBoundary>
        <ClodoviewContext.Provider value={clodoviewContext}>
          <Renderer data={data} template={pos.template} catalog={catalog} />
        </ClodoviewContext.Provider>
      </ErrorBoundary>
      <GoogleGtmNoScript enabled={loadScripts.googleGtm.enabled} />
      <Mouseflow enabled={loadScripts.mouseflow} posKey={pos.key} />
    </>
  )
}

export async function getServerSideProps(context: any) {
  loadContext(context.req)
  const businessUnit = getBusinessUnitFromURL(context.req.headers.host)
  const gtmId = businessUnit === `VIVAREAL` ? GTM_ID_VR : GTM_ID_OLX
  const logger = createRequestLogger(context.req)
  logger.info(parseObject(context.req.headers, `headers`), `Starting`)

  const response = await getPOS(context.req, logger)

  if (REDIRECT_STATUS_LIST.includes(response.status)) {
    const destination =
      response.headers.get(`location`) || `https://oops.olx.com.br`
    const message = `Redirecting to ${destination}`
    const details = {
      'http.response.status_code': response.status,
      'http.response.headers.location': destination,
      'error.type': `Redirect`,
      'error.message': `Redirecting to ${destination}`,
    }
    sendToDataDog(message, details)
    logger.error(details, `Finished`)
    return {
      redirect: {
        permanent: response.status === HTTP_STATUS_CODE.Moved_Permanently,
        destination: destination,
      },
    }
  }

  const pos: Pos = await tryParseJson(response, logger)

  if (!pos || !pos.key || !pos.template || !pos.template.length) {
    const message = `Error when fetching the POS in the pos-render`
    const details = {
      'http.response.status_code': response.status,
      'error.type': `FetchError`,
      'error.message': message,
    }
    sendToDataDog(message, details)
    logger.error(details, `Finished`)
    return {
      notFound: true,
    }
  }

  if (!pos.data) pos.data = {}
  context.req.env[`clodoviewKey`] = pos.data[`clodoviewKey`]
  if (!context.req.env[`type`]) {
    context.req.env[`type`] = pos.data[`type`]
  }
  if (!context.req.env[`listId`] && pos.data[`listId`]) {
    context.req.env[`listId`] = pos.data[`listId`]
  }
  context.req.env[`reason`] = context?.query?.reason || ``

  const hashedUserData: HashedUserData = buildHashedUserData(context.req.env)

  // Clear raw PII from req.env before it is serialized into pos.data.env and sent to the client
  delete context.req.env[`userPhone`]
  delete context.req.env[`userEmail`]
  delete context.req.env[`userZipcode`]

  const dataDogData = { ...pos.data, ...context.req.env }
  delete dataDogData[`buyUrl`]
  delete dataDogData[`publicAccountId`]
  delete dataDogData[`products`]
  pos.data[`env`] = context.req.env

  pos.data[`version`] = VERSION

  const loadScripts = {
    lurker: ENABLE_LURKER,
    googleOptimize: ENABLE_GOOGLE_OPTIMIZE,
    googleGtm: {
      enabled: ENABLE_GOOGLE_GTM,
      id: gtmId,
    },
    webVitals: ENABLE_WEB_VITALS,
    mouseflow: ENABLE_MOUSEFLOW,
  }

  const user = {
    public_id: context.req.env[`publicAccountId`],
  }

  const abVariants = context.req.cookies[`TestAB_Groups`] || ``

  const datadogConfig = { ...DD, posData: dataDogData, user, abVariants }

  logger.info(
    {
      'http.response.status_code': 200,
    },
    `Finished`
  )

  return {
    props: {
      pos,
      loadScripts,
      datadogConfig,
      businessUnit,
      publicAccountId: user.public_id,
      hashedUserData,
      testAbGroups: abVariants,
    },
  }
}
